import { forwardRef } from 'react';

import { FormFieldMessages } from '../../../forms/forms-core/components/form-field-messages/FormFieldMessages.js';
import { FormField } from '../../../forms/forms-core/components/form-field/FormField.js';
import { FilterField } from '../../filter-field/FilterField.js';
import type { FilterFieldRef } from '../../filter-field/FilterField.js';
import type { FilterFieldProps } from '../../filter-field/FilterField.types.js';
import { useFilterComposition } from '../store/useFilterComposition.js';
import { useFilterCompositionState } from '../store/useFilterCompositionState.js';

const DISPLAY_NAME = 'FilterComposition.FilterField';

/**
 * Accepted properties for the FilterField slot inside FilterComposition.
 *
 * Value-owning props (`value`, `defaultValue`, `onChange`, `validatorMap`) are
 * managed by the composition store and are not accepted here, and `onFilter` is
 * owned by the composition's apply lifecycle — observe applies via
 * `FilterComposition`'s `onApply`. Internal Form wiring props (`_form`,
 * `_messageTarget`) are set by the composition itself.
 * @public
 */
export type FilterCompositionFilterFieldProps = Omit<
  FilterFieldProps,
  | 'value'
  | 'defaultValue'
  | 'onChange'
  | 'onFilter'
  | 'validatorMap'
  | '_form'
  | '_messageTarget'
  | 'parserConfig'
>;

/**
 * FilterField slot for use inside FilterComposition.
 *
 * Forwards display-only props to the underlying {@link FilterField} component
 * and acts as a recognized slot so FilterComposition can place it in canonical
 * order. Value + validation wiring is owned by the composition store.
 *
 * Renders nothing when it is not inside a `<FilterComposition>`.
 *
 * @public
 */
export const FilterCompositionFilterField = /* @__PURE__ */ forwardRef<
  FilterFieldRef,
  FilterCompositionFilterFieldProps
>((props, ref) => {
  const store = useFilterComposition();
  const value = useFilterCompositionState((state) => state.filterField);
  // The validator map lives outside reactive state, so this selector ignores its
  // `state` argument and reads the controller instead. It still re-evaluates on
  // every store notification, and `setValidatorMap` emits one; the default
  // `Object.is` comparison then gates the re-render on the map identity.
  // `parserConfig` needs no equivalent — it never changes after construction.
  const validatorMap = useFilterCompositionState(
    () => store?.filterField.validatorMap,
  );

  // Every hook above stays unconditional so hook order is stable; an
  // unresolved store then renders nothing rather than a broken control.
  if (store === undefined) {
    return null;
  }

  return (
    <FormField>
      <FilterField
        {...props}
        ref={ref}
        value={value}
        validatorMap={validatorMap}
        parserConfig={store.filterField.parserConfig}
        onChange={(nextValue) => store.filterField.setValue(nextValue)}
        // The composition's single submit path. Supplying `onFilter` also stops
        // the FilterField from requesting a native submit of its own, so one
        // keystroke runs the apply lifecycle once. The callback fires whether
        // the draft is valid or not, which is what lets an invalid Ctrl+Enter
        // still report through `onApply` and focus the first invalid control.
        onFilter={() => void store._apply('submit')}
        _messageTarget="form"
      />
      <FormFieldMessages />
    </FormField>
  );
});

(
  FilterCompositionFilterField as typeof FilterCompositionFilterField & {
    displayName: string;
  }
).displayName = DISPLAY_NAME;
